import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Landmark, Loader2 } from "lucide-react";

const empty = {
  mobile: "", first_name: "", middle_name: "", last_name: "", product_type: "",
  loan_amount: "", alt_phone: "", dob: "", email: "", pan: "", employment_type: "",
  lead_source: "", city: "", pincode: "",
};

const Field = ({ label, required, children }) => (
  <div className="space-y-1">
    <Label className="text-xs font-medium">{label}{required && <span className="text-red-500"> *</span>}</Label>
    {children}
  </div>
);

export default function PublicEnquiry() {
  const [options, setOptions] = useState({});
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/crm/public/enquiry-meta")
      .then(({ data }) => setOptions(data.options || {}))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.mobile.trim()) return toast.error("Mobile is required");
    if (!Number(form.loan_amount)) return toast.error("Required Loan Amount is required");
    setBusy(true);
    try {
      const { data } = await api.post("/crm/public/enquiry", { ...form, lead_source: form.lead_source || "QR Code" });
      setDone(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const sel = (k, opts, testid) => (
    <select value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={testid}
            className="h-10 w-full rounded-md border border-input bg-white px-2 text-sm">
      <option value="">Select…</option>
      {(opts || []).map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4" data-testid="enquiry-success">
        <div className="w-full max-w-md rounded-2xl border border-border bg-white p-8 text-center shadow-sm">
          <CheckCircle2 className="mx-auto h-14 w-14 text-green-600" />
          <h1 className="mt-4 font-display text-2xl font-bold">Thank you!</h1>
          <p className="mt-2 text-sm text-muted-foreground">{done.message}</p>
          <p className="mt-1 text-xs text-muted-foreground">Reference: <b>{done.enquiry_id}</b></p>
          <Button className="mt-6" onClick={() => { setForm(empty); setDone(null); }} data-testid="new-enquiry-btn">
            Submit another enquiry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="mx-auto w-full max-w-3xl">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <Landmark className="h-6 w-6" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight">Loan Enquiry</h1>
            <p className="text-sm text-muted-foreground">Fill in your details and our team will reach out to you.</p>
          </div>
        </div>
        <form onSubmit={submit} className="rounded-2xl border border-border bg-white p-6 shadow-sm"
              data-testid="public-enquiry-form">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Mobile" required>
              <Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} className="bg-white" data-testid="pub-mobile" /></Field>
            <Field label="First Name">
              <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="bg-white" data-testid="pub-first-name" /></Field>
            <Field label="Middle Name">
              <Input value={form.middle_name} onChange={(e) => set("middle_name", e.target.value)} className="bg-white" data-testid="pub-middle-name" /></Field>
            <Field label="Last Name">
              <Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="bg-white" data-testid="pub-last-name" /></Field>
            <Field label="Product Type">{sel("product_type", options.product_type, "pub-product-type")}</Field>
            <Field label="Required Loan Amount" required>
              <div className="flex items-center rounded-md border border-input bg-white pl-2">
                <span className="text-sm text-muted-foreground">₹</span>
                <Input type="number" value={form.loan_amount} onChange={(e) => set("loan_amount", e.target.value)}
                       className="border-0 bg-transparent" data-testid="pub-loan-amount" />
              </div></Field>
            <Field label="Alternate Phone">
              <Input value={form.alt_phone} onChange={(e) => set("alt_phone", e.target.value)} className="bg-white" data-testid="pub-alt-phone" /></Field>
            <Field label="Date of Birth">
              <Input type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} className="bg-white" data-testid="pub-dob" /></Field>
            <Field label="Email">
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-white" data-testid="pub-email" /></Field>
            <Field label="PAN No.">
              <Input value={form.pan} onChange={(e) => set("pan", e.target.value.toUpperCase())} className="bg-white" data-testid="pub-pan" /></Field>
            <Field label="Employment Type">{sel("employment_type", options.employment_type, "pub-employment-type")}</Field>
            <Field label="Lead Source">{sel("lead_source", options.lead_source, "pub-lead-source")}</Field>
            <Field label="City">
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} className="bg-white" data-testid="pub-city" /></Field>
            <Field label="Pincode">
              <Input value={form.pincode} onChange={(e) => set("pincode", e.target.value)} className="bg-white" data-testid="pub-pincode" /></Field>
          </div>
          <Button type="submit" disabled={busy} className="mt-6 w-full sm:w-auto" data-testid="pub-submit-btn">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Submit Enquiry
          </Button>
        </form>
      </div>
    </div>
  );
}
